import { Badge, Flex, Space, Table, Tag, Typography } from "antd";
import type { ColumnType } from "antd/es/table";
import type { GetRowKey } from "antd/es/table/interface";
import dayjs from "dayjs";
import type React from "react";
import { useMemo } from "react";
import { decimalFormater } from "std/format";
import { roundAbnt } from "std/math";
import type { ApiRelForaPadraoGrupo, ApiRelForaPadraoItem, ApiRelForaPadraoResponse } from "../../types/ApiRelForaPadrao";

const { Title, Text } = Typography;

/* ========================= helpers ========================= */
const safeKey =
	<T,>(prefix: string): GetRowKey<T> =>
	(record: T, index?: number) => {
		const r = record as any;
		const k =
			r?.id ??
			r?.groupId ??
			r?.clti_idpk ??
			r?.data ??
			r?.rota_id ?? // use rota_id (ou rot_idpk se for o seu campo)
			r?.produtor_id ??
			r?.produto_id ??
			r?.for_idpk;

		return k != null ? String(k) : `${prefix}-${index ?? 0}`;
	};

const pctTag = (pct: number, isOutlier: boolean) => {
	const v = roundAbnt(Number.isFinite(pct) ? pct : 0, 2);
	const label = `${v >= 0 ? "+" : ""}${v}%`;

	if (v > 0) return <Tag color={isOutlier ? "green" : "blue"}>{label}</Tag>;

	if (v < 0) return <Tag color={isOutlier ? "volcano" : "gold"}>{label}</Tag>;

	return <Tag>{label}</Tag>;
};

const GroupHeader: React.FC<{ g: ApiRelForaPadraoGrupo }> = ({ g }) => (
	<Space size={16} wrap>
		<Text strong>{g.groupLabel}</Text>
		<Badge status={g.is_outlier ? "error" : "default"} text={g.is_outlier ? "Fora do padrão" : "Normal"} />
	</Space>
);

/** Calcula média simples do mês (total do mês / nº de dias com coleta). */
function calcMediaDoMes(dias: ApiRelForaPadraoItem[]): number {
	const soma = roundAbnt(
		dias.reduce((acc, d) => acc + (d.totalDia || 0), 0),
		3,
	);
	const qtdDias = dias.length || 1;
	return roundAbnt(soma / qtdDias, 3);
}

/* ========================= colunas ========================= */
export const columnsGrupo = (metaThreshold: number): ColumnType<ApiRelForaPadraoGrupo>[] => [
	{
		title: "Grupo",
		dataIndex: "groupLabel",
		key: "groupLabel",
		render: (_val, record) => <GroupHeader g={record} />,
	},
	{
		title: "Mês Atual (Volume)",
		dataIndex: "currentMonthTotal",
		key: "currentMonthTotal",
		align: "right",
		width: 160,
		render: (v: number) => decimalFormater(v || 0, 3),
	},
	{
		title: "Mês Anterior (Volume)",
		dataIndex: "previousMonthTotal",
		key: "previousMonthTotal",
		align: "right",
		width: 180,
		render: (v: number) => decimalFormater(v || 0, 3),
	},
	{
		title: `Variação vs Mês Ant. (≥ ${metaThreshold || 20}% destaca)`,
		dataIndex: "deltaPct",
		key: "deltaPct",
		align: "center",
		width: 200,
		render: (_: number, r: ApiRelForaPadraoGrupo) => pctTag(r.deltaPct, r.is_outlier),
	},
];

export const columnsItem = (media): ColumnType<ApiRelForaPadraoItem>[] => [
	{
		title: "Data",
		dataIndex: "data",
		key: "data",
		width: 140,
		render: (d: string) => (d ? dayjs(String(d)).format("DD/MM/YYYY") : ""),
	},
	{
		title: (
			<Flex vertical align="flex-end" justify="center">
				<span style={{ marginInlineEnd: 12 }}>Volume do dia</span>
				<Tag style={{ marginInlineStart: 8 }}>Média do mês: {decimalFormater(media, 3)}</Tag>
			</Flex>
		),
		dataIndex: "totalDia",
		key: "totalDia",
		align: "right",
		width: 260,
		render: (v: number, row: any) => (
			<Space size={8} style={{ justifyContent: "flex-end", width: "100%" }}>
				<span>{decimalFormater(v || 0, 3)}</span>

				{pctTag(row.deltaPct, row.is_outlier_day > 20)}
			</Space>
		),
	},
];

/** Tabela de dias: destaca acima/abaixo da média */
const DiasTable: React.FC<{ dias: ApiRelForaPadraoItem[] }> = ({ dias }) => {
	const total = roundAbnt(
		dias.reduce((acc, d) => acc + (d.totalDia || 0), 0),
		3,
	);
	const media = useMemo(() => calcMediaDoMes(dias), [dias]);

	return (
		<Table
			rowKey={(r) => r.data}
			columns={columnsItem(media)}
			dataSource={dias}
			size="small"
			pagination={false}
			rowClassName={(row: any) => (row.is_outlier_day ? "row-outlier-dia" : "")}
			summary={() => (
				<Table.Summary.Row>
					<Table.Summary.Cell index={0} align="right">
						<Text strong>Total do mês:</Text>
					</Table.Summary.Cell>

					<Table.Summary.Cell index={1} align="right">
						<Text strong>{decimalFormater(total, 3)}</Text>
					</Table.Summary.Cell>
				</Table.Summary.Row>
			)}
		/>
	);
};
/* ========================= tabela principal ========================= */
export function TableRelForaPadrao({ data, title }: { data: ApiRelForaPadraoResponse | undefined; title: string }) {
	const registros = data?.registros ?? [];
	const meta = data?.meta;

	const totalAtual = roundAbnt(
		registros.reduce((acc, g) => acc + (g?.currentMonthTotal || 0), 0),
		3,
	);
	const totalAnterior = roundAbnt(
		registros.reduce((acc, g) => acc + (g?.previousMonthTotal || 0), 0),
		3,
	);
	const mediaAtual = totalAtual > 0 ? roundAbnt(totalAtual / Math.max(registros.length || 0, 1), 3) : 0;

	return (
		<div className="w-full mt-3">
			<Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
				<div>
					<Title level={4} style={{ margin: 0 }}>
						{title ?? "Relatório de Coletas Mensais"}
					</Title>

					{meta ? (
						<Text type="secondary">
							Período {dayjs(meta.range.start).format("DD/MM/YYYY")} — {dayjs(meta.range.end).format("DD/MM/YYYY")} · Comparação{" "}
							{dayjs(meta.compareWithPrevMonth.start).format("DD/MM/YYYY")} —{" "}
							{dayjs(meta.compareWithPrevMonth.end).format("DD/MM/YYYY")} · Threshold: {meta.thresholdPct || 20}%
						</Text>
					) : null}
				</div>

				<Space>
					<Tag>Qtd. produtores: {registros.length}</Tag>
					<Tag>Mês atual: {decimalFormater(totalAtual, 3)}</Tag>
					<Tag>Mês anterior: {decimalFormater(totalAnterior, 3)}</Tag>
					<Tag>Média/produtor: {decimalFormater(mediaAtual, 3)}</Tag>
				</Space>
			</Flex>

			<Table<ApiRelForaPadraoGrupo>
				rowKey={safeKey("grp")}
				columns={columnsGrupo(meta?.thresholdPct ?? 20)}
				dataSource={registros ?? []}
				size="small"
				pagination={false}
				rowClassName={(record) => (record.is_outlier ? "row-outlier" : "")}
				expandable={{
					rowExpandable: (row) => (row?.dias?.length ?? 0) > 0,
					expandedRowRender: (record) => <DiasTable dias={record?.dias || []} />,
					defaultExpandAllRows: false,
				}}
				summary={(rows) => {
					const totalAtualSum = roundAbnt(
						(rows || []).reduce((acc, cur) => acc + (cur?.currentMonthTotal || 0), 0),
						3,
					);
					const totalAnteriorSum = roundAbnt(
						(rows || []).reduce((acc, cur) => acc + (cur?.previousMonthTotal || 0), 0),
						3,
					);
					const pct =
						totalAnteriorSum === 0
							? totalAtualSum > 0
								? 100
								: 0
							: roundAbnt(((totalAtualSum - totalAnteriorSum) / totalAnteriorSum) * 100, 2);

					return (
						<Table.Summary.Row>
							<Table.Summary.Cell index={0} colSpan={2}>
								<Text strong>Totais:</Text>
							</Table.Summary.Cell>

							<Table.Summary.Cell index={1} align="right">
								<Text strong>{decimalFormater(totalAtualSum, 3)}</Text>
							</Table.Summary.Cell>

							<Table.Summary.Cell index={2} align="right">
								<Text strong>{decimalFormater(totalAnteriorSum, 3)}</Text>
							</Table.Summary.Cell>

							<Table.Summary.Cell index={3} align="center">
								<Text strong>{`${pct >= 0 ? "+" : ""}${pct}%`}</Text>
							</Table.Summary.Cell>
						</Table.Summary.Row>
					);
				}}
			/>

			<style>
				{`
          /* grupo fora do padrão (já tinha) */
          .row-outlier > td {
            background: rgba(250, 84, 28, 0.08);
          }
          /* linhas dos dias */
          .row-outlier-dia > td {
            background: rgba(250, 84, 28, 0.08);
          }
          /* destaque no valor da célula quando acima/abaixo */
          .cell-dia-acima {
            font-weight: 600;
          }
          .cell-dia-abaixo {
            font-weight: 600;
          }
        `}
			</style>
		</div>
	);
}
